<template>
	<div ref="jumpLinkNavRootRef">
		<div
			v-if="props.jumpLinks.length"
			:class="[
				'jump-link-nav position-sticky',
				`bg-color-${props.backgroundColor}`,
				{ stickyBorder: isSticky, nonStickyHeader: !props.stickyHeader || appStore.isMobileWebview }
			]"
		>
			<div ref="jumpLinkPlaceholderElemRef"></div>
			<div class="container">
				<div
					:class="[
						'overflow-scroll overflow-scroll-no-wrap display-flex justify-content-space-between align-items-center width-100',
						{ bb: props.setBorder && !isSticky },
						props.classes
					]"
					data-test="jump-link-nav-wrapper"
				>
					<div class="jump-link-nav--list" role="tablist">
						<button
							v-for="link in props.jumpLinks"
							:id="link.key"
							:key="link.key"
							role="tab"
							class="jump-link-nav--list-item"
							:data-test="`jump-link-${link.key}`"
							:disabled="isActive(link.key)"
							@click="handleSelectedLink(link)"
						>
							{{ link.label }}
						</button>
					</div>
				</div>
			</div>
		</div>
		<div ref="jumpLinkSlotRef">
			<slot />
		</div>
	</div>
</template>
<script setup lang="ts">
import { computed, onMounted, ref, Ref } from 'vue';
import { JumpLink } from 'types/layout';
import { useAppStore } from '@stores/app';
import { useMixpanel } from '@utils/composables/use-mixpanel';
import { useRoute } from 'vue-router';

interface Props {
	jumpLinks: Array<JumpLink>;
	setBorder?: boolean;
	rootMargin?: number;
	stickyHeader?: boolean;
	classes?: string;
	backgroundColor?: string;
	mixpanelContext?: string;
}

const props = withDefaults(defineProps<Props>(), {
	setBorder: true,
	rootMargin: 0,
	stickyHeader: true,
	classes: '',
	backgroundColor: 'white',
	mixpanelContext: undefined
});

const route = useRoute();
const { trackMixpanelClick } = useMixpanel();

const appStore = useAppStore();

const jumpLinkNavRootRef: Ref<HTMLElement | null> = ref(null);
const jumpLinkPlaceholderElemRef: Ref<HTMLElement | null> = ref(null);
const jumpLinkSlotRef: Ref<HTMLElement | null> = ref(null);

const isSticky = ref(false);
const stickyJumpLinkNavObserver: Ref<IntersectionObserver | null> = ref(null);
const currentSection = ref(props.jumpLinks[0]?.key ?? '');

const hasBanner = computed((): boolean => !!appStore.currentBanner);
const stickyBorderRootMargin = computed((): string => (hasBanner.value ? '-150px' : '-100px'));

async function handleSelectedLink(link: JumpLink): Promise<void> {
	const el = jumpLinkNavRootRef.value?.querySelector(`[data-context="${link.key}"]`);
	el?.scrollIntoView({ behavior: 'smooth', block: 'start' });

	trackMixpanelClick({
		'Action Element': 'Tab',
		'Action Target': link.label,
		'Action Context': props.mixpanelContext
	});
}

function isActive(key: string) {
	return currentSection.value === key;
}

function stickyNavIntersect(entries: Array<IntersectionObserverEntry>) {
	isSticky.value = entries[0].intersectionRatio !== 1;
}

function setActiveLink(): void {
	const links = jumpLinkNavRootRef.value?.querySelectorAll('.jump-link-nav--list-item');
	const scrollY = window.scrollY;

	props.jumpLinks.forEach((link) => {
		const linkSlotElem = jumpLinkSlotRef.value?.querySelector(`#${link.key}`);
		if (linkSlotElem && scrollY >= (linkSlotElem as HTMLElement).offsetTop - props.rootMargin) {
			currentSection.value = linkSlotElem.getAttribute('id') ?? '';
		}
	});

	links?.forEach((link) => {
		if (link.getAttribute('id') === currentSection.value) {
			jumpLinkNavRootRef.value?.querySelector('.jump-link-nav--list-item.active')?.classList.remove('active');
			link.classList.add('active');
		}
	});
}

onMounted(async () => {
	stickyJumpLinkNavObserver.value = new IntersectionObserver(stickyNavIntersect, {
		threshold: 1,
		rootMargin: stickyBorderRootMargin.value
	});
	if (jumpLinkPlaceholderElemRef.value) {
		stickyJumpLinkNavObserver.value.observe(jumpLinkPlaceholderElemRef.value);
	}

	if (props.jumpLinks.length > 0) {
		jumpLinkNavRootRef.value?.querySelector(`#${currentSection.value}`)?.classList.add('active');
		if (route.hash) {
			const activeJumpLink = props.jumpLinks.find((link) => link.key === route.hash.slice(1));
			if (activeJumpLink) {
				await handleSelectedLink(activeJumpLink);
			}
		}
		window.addEventListener('scroll', setActiveLink);

		props.jumpLinks.forEach((link) => {
			const linkSlotElem = jumpLinkSlotRef.value?.querySelector(`#${link.key}`);
			linkSlotElem?.classList.add('jump-link-nav--section');
		});
	}
});
</script>

<style lang="scss" scoped>
@use '../../styles/constants/colors' as *;
@use '../../styles/constants/typography' as *;
@use '../../styles/utilities/respond-to.scss' as *;

$desktop-nav-height: 68px;
$mobile-nav-height: 48px;

.jump-link-nav {
	top: $mobile-nav-height;
	padding-top: 20px;
	z-index: 4;

	@include respond-to(lg) {
		top: $desktop-nav-height;
	}

	&.nonStickyHeader {
		top: 0;
	}

	&.stickyBorder {
		border-bottom: 1px solid token('bg-tertiary');
	}

	&--list {
		color: token('content-secondary');
		display: flex;
		font-family: $base-font-family;
		flex-direction: row;
		justify-content: left;
		align-items: flex-start;
		gap: 2rem;

		&-item {
			background: transparent;
			border: none;
			border-radius: 0;
			font-size: 16px;
			line-height: 1.5;
			padding: 0 4px 8px;
			position: relative;

			&::before {
				background: token('content-primary');
				bottom: 0;
				content: '';
				height: 2px;
				left: 0;
				opacity: 0;
				position: absolute;
				transition: all 300ms ease;
				width: 100%;
			}

			&.active {
				color: token('content-primary');

				&::before {
					opacity: 1;
				}
			}

			@media (hover: hover) {
				// prevents hover state on touch devices
				&:hover {
					&::before {
						opacity: 1;
					}
				}
			}

			&:focus-visible {
				color: token('content-primary');
				outline: -webkit-focus-ring-color auto 1px;
			}
		}
	}

	&--section {
		scroll-margin-top: 120px;
	}
}
</style>
